import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import api from "@/lib/api";
import { getErrorMessage } from "@/lib/errors";
import { Card } from "@/components/ui/card";
import { Switch } from "@/components/ui/switch";
import { InlineError } from "@/components/ListState";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";

// Round 29 §5.5: mail per kind and per person. A switch that shows "aan" for a
// mail the server cannot send is a lie, so every row says what the server does.
export const MailVoorkeuren = ({ onP1Status }) => {
  const [state, setState] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const [busy, setBusy] = useState(false);
  const [dialog, setDialog] = useState(null); // {kind:"soort"} | {kind:"persoon", userId}

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/settings/mail-voorkeuren");
      setState(data);
      // P1 / §5.5: the banner belongs at the TOP of the screen, above the status
      // card, so the PAGE owns it. This section only reports the verdict.
      onP1Status?.(!!data.p1_bereikt_niemand);
      setError(false);
    } catch (e) {
      setError(true);
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); }, []);

  const put = async (soorten, gebruikers) => {
    setBusy(true);
    try {
      const { data } = await api.put("/settings/mail-voorkeuren", { soorten, gebruikers });
      setState(data);
      onP1Status?.(!!data.p1_bereikt_niemand);
      toast.success("Mailvoorkeuren opgeslagen");
    } catch (e) {
      toast.error(getErrorMessage(e, "Opslaan mislukt."));
      await load();
    } finally { setBusy(false); }
  };

  const currentSoorten = () => {
    const out = {};
    (state.soorten || []).forEach((s) => { out[s.kind] = s.aan; });
    return out;
  };
  const currentGebruikers = () => {
    const out = {};
    (state.gebruikers || []).forEach((u) => { out[u.id] = { ...u.voorkeuren }; });
    return out;
  };

  const setKind = (kind, aan) => put({ ...currentSoorten(), [kind]: aan }, currentGebruikers());

  const setPerson = (userId, kind, aan) => {
    const gebruikers = currentGebruikers();
    gebruikers[userId] = { ...(gebruikers[userId] || {}), [kind]: aan };
    put(currentSoorten(), gebruikers);
  };

  // m2/S7: "nobody gets a P1 mail" is the SERVER's verdict. isLastP1Person only
  // decides whether to ask first, and it must agree with that verdict: it counts
  // people who could actually receive one.
  const isLastP1Person = (userId) => {
    const p1Kind = (state.soorten || []).find((s) => s.kind === "fault_p1");
    if (!p1Kind?.aan || !state.email_configured) return false;
    const aan = (state.gebruikers || []).filter((u) => u.voorkeuren.fault_p1 && u.email);
    return aan.length === 1 && aan[0].id === userId;
  };

  if (loading) {
    return <p className="text-body text-muted-foreground" data-testid="mail-voorkeuren-loading">Mailvoorkeuren laden…</p>;
  }
  if (error || !state) {
    return <InlineError message="Kan de mailvoorkeuren niet laden." onRetry={load} testid="mail-voorkeuren-error" />;
  }

  const serverNote = (s) => {
    if (!state.email_configured) return "Er is geen mailsleutel actief op de server — deze mail gaat niet, wat je hier ook kiest.";
    if (s.geschduld && !state.scheduler_enabled) return "Staat uit op de server — deze mail gaat niet, wat je hier ook kiest.";
    return null;
  };

  const confirmText = dialog?.kind === "persoon"
    ? "Dit is de laatste die nog een mail krijgt bij een P1-storing. Daarna krijgt niemand er nog een. Het belletje blijft werken."
    : "Niemand krijgt dan nog een mail bij een P1-storing. Het belletje blijft werken.";

  return (
    <div className="space-y-4" data-testid="mail-voorkeuren">
      <Card className="p-5">
        <h2 className="mb-1 text-body font-semibold text-foreground">Welke mails gaan er uit</h2>
        <p className="mb-4 text-data text-muted-foreground">
          Zet je een soort uit, dan gaat die mail naar niemand. De melding blijft altijd in de app staan.
        </p>
        <ul className="divide-y divide-border">
          {(state.soorten || []).map((s) => (
            <li key={s.kind} className="flex flex-wrap items-center justify-between gap-3 py-3" data-testid={`mail-soort-${s.kind}`}>
              <div className="min-w-0">
                <div className="text-body text-foreground">{s.label}</div>
                {serverNote(s) && (
                  <p className="text-data text-muted-foreground" data-testid={`mail-soort-servernote-${s.kind}`}>{serverNote(s)}</p>
                )}
              </div>
              <Switch
                data-testid={`mail-soort-switch-${s.kind}`}
                checked={s.aan}
                disabled={busy}
                onCheckedChange={(v) => {
                  if (s.kind === "fault_p1" && !v) { setDialog({ kind: "soort" }); return; }
                  setKind(s.kind, v);
                }}
              />
            </li>
          ))}
        </ul>
      </Card>

      <Card className="p-5">
        <h2 className="mb-1 text-body font-semibold text-foreground">Wie krijgt wat</h2>
        <p className="mb-4 text-data text-muted-foreground">
          Per persoon. Iemand krijgt een mail als de soort aan staat én bij die persoon aan staat.
        </p>

        <div className="hidden sm:block">
          <Table data-testid="mail-personen-table">
            <TableHeader>
              <TableRow>
                <TableHead>Persoon</TableHead>
                {(state.soorten || []).map((s) => (
                  <TableHead key={s.kind}>{s.label.split(" — ")[0]}</TableHead>
                ))}
              </TableRow>
            </TableHeader>
            <TableBody>
              {(state.gebruikers || []).map((u) => (
                <TableRow key={u.id} data-testid={`mail-persoon-row-${u.id}`}>
                  <TableCell>
                    <div className="text-body text-foreground">{u.name}</div>
                    <div className="text-data text-muted-foreground">{u.email}</div>
                  </TableCell>
                  {(state.soorten || []).map((s) => (
                    <TableCell key={s.kind}>
                      <Switch
                        data-testid={`mail-persoon-switch-${u.id}-${s.kind}`}
                        checked={u.voorkeuren[s.kind]}
                        disabled={busy}
                        onCheckedChange={(v) => {
                          if (s.kind === "fault_p1" && !v && isLastP1Person(u.id)) {
                            setDialog({ kind: "persoon", userId: u.id });
                            return;
                          }
                          setPerson(u.id, s.kind, v);
                        }}
                      />
                    </TableCell>
                  ))}
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>

        <ul className="space-y-3 sm:hidden">
          {(state.gebruikers || []).map((u) => (
            <li key={u.id} className="border border-lijn-zacht p-3" data-testid={`mail-persoon-card-${u.id}`}>
              <div className="text-body font-semibold text-foreground">{u.name}</div>
              <div className="text-data text-muted-foreground">{u.email}</div>
              <ul className="mt-2 divide-y divide-border">
                {(state.soorten || []).map((s) => (
                  <li key={s.kind} className="flex items-center justify-between gap-3 py-2">
                    <span className="text-body text-foreground">{s.label.split(" — ")[0]}</span>
                    <Switch
                      data-testid={`mail-persoon-card-switch-${u.id}-${s.kind}`}
                      checked={u.voorkeuren[s.kind]}
                      disabled={busy}
                      onCheckedChange={(v) => {
                        if (s.kind === "fault_p1" && !v && isLastP1Person(u.id)) {
                          setDialog({ kind: "persoon", userId: u.id });
                          return;
                        }
                        setPerson(u.id, s.kind, v);
                      }}
                    />
                  </li>
                ))}
              </ul>
            </li>
          ))}
        </ul>
      </Card>

      <AlertDialog open={!!dialog} onOpenChange={(o) => !o && setDialog(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>P1-mail uitzetten?</AlertDialogTitle>
            <AlertDialogDescription>{confirmText}</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="mail-p1-uit-cancel">Laten staan</AlertDialogCancel>
            <AlertDialogAction
              data-testid="mail-p1-uit-confirm"
              onClick={() => {
                const d = dialog;
                setDialog(null);
                if (d.kind === "persoon") setPerson(d.userId, "fault_p1", false);
                else setKind("fault_p1", false);
              }}
            >
              Uitzetten
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};
